import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getDisputes, resolveDispute, getOpenRequests, settleRequest } from '../utils/api';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  Shield,
  User,
  Briefcase
} from 'lucide-react';
import '../styles/DisputesPage.css';

const DisputesPage = () => {
  const [activeTab, setActiveTab] = useState('open');
  const { success, error } = useToast();
  const [resolveModal, setResolveModal] = useState({ open: false, dispute: null });
  const [resolution, setResolution] = useState('');
  const [resolutionType, setResolutionType] = useState('resolved');
  const [settlePercent, setSettlePercent] = useState(50);
  const [detailModal, setDetailModal] = useState(null); // disputa em consulta

  const { data: ticketDisputes, isLoading, execute: fetchDisputes } = useApi(getDisputes, { immediate: true });
  const { data: allRequests, execute: fetchRequests } = useApi(getOpenRequests, { immediate: true });

  // Disputas de PAGAMENTO faseado (pedidos com payment_status='disputed')
  const paymentDisputes = (allRequests || [])
    .filter(r => r.payment_status === 'disputed')
    .map(r => ({
      id: `req-${r.request_id}`,
      request_id: r.request_id,
      client_name: r.client_name,
      worker_name: r.worker_name || '—',
      reason: 'Recusa de parcela (pagamento faseado)',
      severity: 'high',
      status: 'open',
      created_at: r.created_at,
      isPayment: true,
      agreed_price: r.agreed_price,
      released_percent: r.released_percent,
    }));

  const disputes = [...paymentDisputes, ...(ticketDisputes || [])];

  const handleResolve = async () => {
    const d = resolveModal.dispute;
    if (!d) return;
    try {
      if (d.isPayment) {
        // Disputa de pagamento → decide a % final do trabalhador (escrow)
        const pct = Number(settlePercent);
        if (isNaN(pct) || pct < 0 || pct > 100) { error('Percentagem inválida (0-100)'); return; }
        await settleRequest(d.request_id, pct);
        success(`Decisão registada: profissional recebe ${pct}%`);
        fetchRequests();
      } else {
        if (!resolution.trim()) return;
        await resolveDispute(d.id, { type: resolutionType, notes: resolution });
        success('Mediação resolvida com sucesso!');
        fetchDisputes();
      }
      setResolveModal({ open: false, dispute: null });
      setResolution('');
    } catch (err) {
      error(err.response?.data?.message || 'Erro ao resolver mediação');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      open: 'bg-red-500/20 text-red-400',
      under_review: 'bg-yellow-500/20 text-yellow-400',
      resolved: 'bg-green-500/20 text-green-400',
      escalated: 'bg-purple-500/20 text-purple-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.open}`}>
        {status === 'under_review' ? 'Em Análise' :
         status === 'escalated' ? 'Escalada' :
         status === 'resolved' ? 'Resolvida' : 'Aberta'}
      </span>
    );
  };

  const severityBadge = (severity) => {
    const styles = {
      low: 'bg-green-500/20 text-green-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      high: 'bg-orange-500/20 text-orange-400',
      critical: 'bg-red-500/20 text-red-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[severity] || styles.low}`}>
        {severity === 'critical' ? 'Crítica' : severity === 'high' ? 'Alta' : severity === 'medium' ? 'Média' : 'Baixa'}
      </span>
    );
  };

  const tabs = [
    { id: 'open', label: 'Abertas', count: (disputes || []).filter(d => d.status === 'open').length },
    { id: 'under_review', label: 'Em Análise', count: (disputes || []).filter(d => d.status === 'under_review').length },
    { id: 'resolved', label: 'Resolvidas', count: (disputes || []).filter(d => d.status === 'resolved').length },
    { id: 'all', label: 'Todas', count: (disputes || []).length },
  ];

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'request_id', label: 'Pedido', width: '90px' },
    { key: 'client_name', label: 'Cliente', render: (row) => (
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-gray-500" />
        <span>{row.client_name}</span>
      </div>
    )},
    { key: 'worker_name', label: 'Profissional', render: (row) => (
      <div className="flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-gray-500" />
        <span>{row.worker_name}</span>
      </div>
    )},
    { key: 'reason', label: 'Motivo' },
    { key: 'severity', label: 'Gravidade', render: (row) => severityBadge(row.severity || 'low') },
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.status) },
    { key: 'created_at', label: 'Aberta', render: (row) =>
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-'
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.status !== 'resolved' && (
            <button
              onClick={() => setResolveModal({ open: true, dispute: row })}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-500/10 text-teal-400 
                         hover:bg-teal-500/20 transition-colors text-sm font-medium"
            >
              <Shield className="w-3.5 h-3.5" />
              Resolver
            </button>
          )}
          <button
            onClick={() => setDetailModal(row)}
            title="Ver detalhes da mediação"
            className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const getTabData = () => {
    if (activeTab === 'all') return disputes || [];
    return (disputes || []).filter(d => d.status === activeTab);
  };

  return (
    <div className="disputes-admin-page space-y-6">
      {/* Header */}
      <div className="disputes-admin-hero"><div><span>Proteção da plataforma</span><h1>Centro de resolução</h1><p>Analise evidências, proteja o valor retido e registe decisões transparentes.</p></div><div className="disputes-admin-held"><Shield/><span><small>Valor em casos de pagamento</small><strong>Kz {paymentDisputes.reduce((sum,d) => sum + Number(d.agreed_price || 0), 0).toLocaleString('pt-PT')}</strong></span></div></div>

      {/* Stats */}
      <div className="disputes-admin-stats grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="dispute-stat dispute-stat--red bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(disputes || []).filter(d => d.status === 'open').length}</p>
              <p className="text-gray-400 text-sm">Abertas</p>
            </div>
          </div>
        </div>
        <div className="dispute-stat dispute-stat--gold bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(disputes || []).filter(d => d.status === 'under_review').length}</p>
              <p className="text-gray-400 text-sm">Em Análise</p>
            </div>
          </div>
        </div>
        <div className="dispute-stat dispute-stat--green bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg"><CheckCircle className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(disputes || []).filter(d => d.status === 'resolved').length}</p>
              <p className="text-gray-400 text-sm">Resolvidas</p>
            </div>
          </div>
        </div>
        <div className="dispute-stat dispute-stat--blue bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg"><Shield className="w-5 h-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(disputes || []).length}</p>
              <p className="text-gray-400 text-sm">Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="disputes-admin-tabs border-b border-gray-700">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative
                ${activeTab === tab.id
                  ? 'text-teal-400 border-b-2 border-teal-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs
                  ${activeTab === tab.id ? 'bg-teal-500/20 text-teal-400' : 'bg-gray-700 text-gray-400'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading && !disputes ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={getTabData()}
          columns={columns}
          pageSize={10}
          emptyMessage="Nenhuma mediação encontrada."
        />
      )}

      {/* Detail Modal (ícone de mensagens) */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setDetailModal(null)}>
          <div className="dispute-admin-modal bg-gray-800 rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              Detalhes da mediação {detailModal.isPayment ? `— Trabalho #${detailModal.request_id}` : `#${detailModal.id}`}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-900/60 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Cliente</p>
                  <p className="text-white">{detailModal.client_name || '—'}</p>
                </div>
                <div className="bg-gray-900/60 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Profissional</p>
                  <p className="text-white">{detailModal.worker_name || '—'}</p>
                </div>
              </div>
              <div className="bg-gray-900/60 rounded-lg p-3">
                <p className="text-gray-500 text-xs mb-1">Motivo</p>
                <p className="text-white">{detailModal.reason || '—'}</p>
              </div>
              {detailModal.description && (
                <div className="bg-gray-900/60 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Descrição</p>
                  <p className="text-gray-300">{detailModal.description}</p>
                </div>
              )}
              {detailModal.isPayment && (
                <div className="bg-gray-900/60 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Pagamento</p>
                  <p className="text-gray-300">
                    Valor acordado: <span className="text-teal-400 font-semibold">Kz {Number(detailModal.agreed_price || 0).toLocaleString('pt-PT')}</span>
                    {' '}· já libertado ao profissional: {Number(detailModal.released_percent || 0)}%
                  </p>
                </div>
              )}
              {detailModal.resolution_notes && (
                <div className="bg-gray-900/60 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Notas de resolução</p>
                  <p className="text-gray-300">{detailModal.resolution_notes}</p>
                </div>
              )}
              <div className="flex items-center gap-3">
                {statusBadge(detailModal.status)}
                {severityBadge(detailModal.severity || 'low')}
                <span className="text-gray-500 text-xs">
                  Aberta em {detailModal.created_at ? new Date(detailModal.created_at).toLocaleDateString('pt-PT') : '—'}
                </span>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              {detailModal.status !== 'resolved' && (
                <button
                  onClick={() => { setResolveModal({ open: true, dispute: detailModal }); setDetailModal(null); }}
                  className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors">
                  Resolver
                </button>
              )}
              <button onClick={() => setDetailModal(null)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {resolveModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="dispute-admin-modal bg-gray-800 rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">
              {resolveModal.dispute?.isPayment ? 'Decidir mediação de pagamento' : 'Resolver mediação'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {resolveModal.dispute?.isPayment
                ? `Trabalho #${resolveModal.dispute?.request_id} — ${resolveModal.dispute?.reason}`
                : `Mediação #${resolveModal.dispute?.id} - ${resolveModal.dispute?.reason}`}
            </p>

            {resolveModal.dispute?.isPayment ? (
              <div className="mb-4">
                <p className="text-sm text-gray-300 mb-1">
                  Valor total: <span className="text-teal-400 font-semibold">Kz {Number(resolveModal.dispute?.agreed_price || 0).toLocaleString('pt-PT')}</span>
                  {Number(resolveModal.dispute?.released_percent) > 0 && (
                    <> · já libertado: {Number(resolveModal.dispute?.released_percent)}%</>
                  )}
                </p>
                <label className="block text-gray-400 text-sm mb-2 mt-3">
                  Percentagem FINAL para o profissional (0–100):
                </label>
                <div className="flex gap-2 mb-2">
                  {[0, 25, 50, 75, 100].map(p => (
                    <button key={p} onClick={() => setSettlePercent(p)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-semibold transition-all
                        ${Number(settlePercent) === p ? 'border-teal-500 bg-teal-500/10 text-teal-400' : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
                      {p}%
                    </button>
                  ))}
                </div>
                <input type="number" min="0" max="100" value={settlePercent}
                  onChange={(e) => setSettlePercent(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                <p className="text-xs text-gray-500 mt-2">
                  Profissional recebe {Number(settlePercent) || 0}% (Kz {(Number(resolveModal.dispute?.agreed_price || 0) * (Number(settlePercent) || 0) / 100).toLocaleString('pt-PT')}); o resto é reembolsado ao cliente.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-2">Tipo de Resolução:</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'resolved', label: 'Resolvida', icon: CheckCircle, color: 'green' },
                      { value: 'refunded', label: 'Reembolsada', icon: XCircle, color: 'red' },
                      { value: 'escalated', label: 'Escalada', icon: AlertTriangle, color: 'purple' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setResolutionType(opt.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all
                          ${resolutionType === opt.value
                            ? `border-${opt.color}-500 bg-${opt.color}-500/10 text-${opt.color}-400`
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}
                      >
                        <opt.icon className="w-4 h-4" />
                        <span className="text-sm">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-1">Notas da Resolução:</label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    rows={4}
                    placeholder="Descreva como a mediação foi resolvida..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white
                               placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setResolveModal({ open: false, dispute: null }); setResolution(''); }}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleResolve}
                disabled={!resolveModal.dispute?.isPayment && !resolution.trim()}
                className="px-4 py-2 rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resolveModal.dispute?.isPayment ? 'Registar Decisão' : 'Resolver mediação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputesPage;
